import type {ForwardRefExoticComponent, RefAttributes} from 'react'

import type {LinkProps} from '@chakra-ui/react'
import {Link as ReactRouterLink} from 'react-router-dom'
import type {LinkProps as ReactRouterLinkProps} from 'react-router-dom'
import {Link as MonomorphicLink} from './chakraMonomorphic'

/**
 * A Chakra `Link` rendered `as` a react-router `Link`.
 *
 * Chakra v1's polymorphic `ComponentWithAs` signature is what normally merges
 * the `as` target's props (here, react-router's `to`) into the call site. That
 * signature is generic over `React.ElementType`, which TypeScript 5 expands
 * past its union-size limit — `TS2590`. Declaring the single concrete shape the
 * app actually uses keeps `to` typed without asking the compiler to represent
 * that union.
 *
 * Type-level cast only: the runtime component is Chakra's `Link`, and call
 * sites still pass `as={ReactRouterLink}` themselves, so nothing about the
 * rendered output changes. The cast is taken from the already-monomorphic
 * `Link` rather than the raw Chakra export — widening a plain
 * `ForwardRefExoticComponent` keeps the two types overlapping, whereas casting
 * straight off `ComponentWithAs` is rejected (TS2352) and would need an
 * `unknown` hop.
 *
 * Delete alongside utils/chakraMonomorphic with the Chakra v2 upgrade
 * (AB#19738), which retypes these components in a form TypeScript 5 can
 * represent.
 */
export const RouterLink = MonomorphicLink as ForwardRefExoticComponent<
  LinkProps &
    Pick<ReactRouterLinkProps, 'to'> & {
      as: typeof ReactRouterLink
    } & RefAttributes<HTMLAnchorElement>
>
